import React from 'react';

/**
 * UnifiedEmptyState — canonical "nothing here yet" surface.
 *
 * Replaces the scattered hand-coded "big centred icon + gray text"
 * blocks (loading placeholders, filtered-away lists, first-time
 * screens) that used to be rewritten per page with inconsistent
 * paddings, radii, and typographic scale.
 *
 * Props:
 *  - icon: lucide-react component (renders in a soft gold-tinted circle)
 *  - title: primary line (empty-state headline)
 *  - description: secondary line (why nothing / next step)
 *  - action: optional CTA node (usually a UnifiedButton)
 *  - variant: 'default' | 'compact' — compact reduces vertical padding
 *  - testId
 */
export const UnifiedEmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  variant = 'default',
  className = '',
  testId,
}) => {
  const paddingY = variant === 'compact' ? 'var(--mesp-space-8)' : 'var(--mesp-space-12)';

  return (
    <div
      className={`mesp-ui ${className}`}
      data-testid={testId}
      style={{
        background: 'var(--mesp-bg-elevated)',
        border: '1px solid var(--mesp-border)',
        borderRadius: 'var(--mesp-radius-lg)',
        padding: `${paddingY} var(--mesp-space-6)`,
        textAlign: 'center',
        color: 'var(--mesp-text-muted)',
      }}
    >
      {Icon && (
        <div
          aria-hidden
          style={{
            width: 56,
            height: 56,
            margin: '0 auto var(--mesp-space-4)',
            borderRadius: 'var(--mesp-radius-pill)',
            background: 'var(--mesp-gold-soft-2)',
            border: '1px solid var(--mesp-gold-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--mesp-gold)',
          }}
        >
          <Icon size={24} strokeWidth={1.75} />
        </div>
      )}
      {title && (
        <h3
          style={{
            margin: 0,
            marginBottom: description ? 'var(--mesp-space-2)' : 0,
            fontSize: 'var(--mesp-font-h4)',
            fontWeight: 'var(--mesp-font-weight-semibold)',
            color: 'var(--mesp-text)',
          }}
        >
          {title}
        </h3>
      )}
      {description && (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--mesp-font-sm)',
            color: 'var(--mesp-text-muted)',
            maxWidth: 480,
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: 1.6,
          }}
        >
          {description}
        </p>
      )}
      {action && (
        <div style={{ marginTop: 'var(--mesp-space-5)', display: 'inline-flex' }}>
          {action}
        </div>
      )}
    </div>
  );
};

export default UnifiedEmptyState;
